import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { startWith } from 'rxjs/operators';

import { GasInputBoolean } from '@cikrf/gas-utils/decorators';

import { GasForm } from '../../gas-form.namespace';
import { GasFormBaseComponent } from '../../gas-form-base-component';
import { GasMathHelper } from '@cikrf/gas-utils/helpers';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

const DISABLED_ATTRIBUTE = 'ng-reflect-native-disabled';

@UntilDestroy()
@Component({
  // eslint-disable-next-line
  selector: 'input[gasRadio][type=radio]',
  templateUrl: './gas-radio.component.html',
  styleUrls: ['./gas-radio.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GasRadioComponent<T = GasForm.GasFilterFormBaseValue> extends GasFormBaseComponent<T> implements AfterViewInit  {

  @ViewChild('template')
  public templateRef: ElementRef;

  /** Заголовок radio кнопки */
  @Input()
  public label: string;

  /** Значение radio кнопки */
  @Input()
  public value: T;

  @Input()
  public error: string;

  @Input()
  public nativeId: string = GasMathHelper.getUniqueId();

  @Input()
  public modified = true;

  @Input()
  @GasInputBoolean()
  public disabled: string | boolean = false;

  @Output('clicked')
  public clicked$ = new EventEmitter<void>();

  public get isModified(): boolean {
    return !this.disabled
      && !this.activeSubject$.value
      && this.isDefaultValue
      && !this.isSuccess()
      && this.modified;
  }

  public get isActive(): boolean {
    return this.activeSubject$.value;
  }

  public get checkDisabled(): boolean {
    const hostElement: HTMLElement = this.elementRef.nativeElement;

    return hostElement.attributes.getNamedItem(DISABLED_ATTRIBUTE)?.value === 'true';
  }

  public inputType = new Subject<GasForm.InputState | string>();

  public form: FormControl;

  public viewMode: 'list' | 'button' = 'list';

  public isDefaultValue = false;

  public active$: Observable<boolean>;

  private activeSubject$ = new BehaviorSubject<boolean>(false);

  constructor(
    public changeDetector: ChangeDetectorRef,
    public elementRef: ElementRef,
    public renderer: Renderer2,
  ) {
    super(changeDetector, elementRef, renderer);

    this.active$ = this.activeSubject$.asObservable();
  }

  public ngAfterViewInit(): void {
    /** Нужно для того, что бы отработал первый раз active$ */
    setTimeout(() => {
      this.wrapElement();

      this.form.valueChanges
        .pipe(
          startWith(this.form.value),
          untilDestroyed(this),
        )
        .subscribe((value: T) => {
          this.setActive(value);
        });

      this.inputType
        .pipe(untilDestroyed(this))
        .subscribe((value: GasForm.InputState) => {
          this.setInputTypeAttribute(value);
        });

      if (this.form.value) {
        this.setActive(this.form.value);
      }

      if (!this.changeDetector['destroyed']) {
        this.changeDetector.detectChanges();
      }
    });
  }

  public onClickHandler(): void {
    if (!this.disabled || !this.checkDisabled) {
      this.clicked$.emit();
    }
  }

  public setInputTypeAttribute(attribute: string): void {
    const element: HTMLElement = this.templateRef.nativeElement;

    this.renderer.setAttribute(element, 'type', attribute);
  }

  public isSuccess(): boolean {
    return this.state === GasForm.InputState.Success;
  }

  public isError(): boolean {
    return this.state === GasForm.InputState.Error;
  }

  public isDefault(): boolean {
    return this.state === GasForm.InputState.Default;
  }

  private setActive(value: T): void {
    this.activeSubject$.next(this.value === value);
  }

  private wrapElement(): void {
    const hostElement: HTMLElement = this.elementRef.nativeElement;
    const templateElement: HTMLElement = this.templateRef.nativeElement;

    /** Оборачивает host-элемент */
    this.renderer.insertBefore(hostElement.parentElement, templateElement, hostElement);
    this.renderer.appendChild(templateElement, hostElement);
  }
}
